import { checkOccupiedUser, tempLogin } from '../base/local';
import { toState } from '../base/redux';
import { setRoomOption } from './actions';
/**
 * 오류/Event 에서 진단에 쓸 핵심 필드를 뽑아 사람이 읽을 수 있는 한 줄로 만든다.
 * DOM Event(WebSocket/미디어 오류)는 그냥 문자열화하면 "[object Event]" 라 정보가 없으므로
 * type/url/readyState/code/reason/name/message 를 명시적으로 추출한다.
 */
const serializeError = (e) => {
    try {
        if (e == null)
            return String(e);
        if (typeof e === 'string')
            return e;
        const parts = [];
        if (e.type)
            parts.push(`type=${e.type}`);
        if (e.name)
            parts.push(`name=${e.name}`);
        if (e.message)
            parts.push(`message=${e.message}`);
        if (e.code !== undefined && e.code !== null)
            parts.push(`code=${e.code}`);
        if (e.reason)
            parts.push(`reason=${e.reason}`);
        const target = e.target || e.currentTarget;
        if (target) {
            if (target.url)
                parts.push(`url=${target.url}`);
            if (target.readyState !== undefined)
                parts.push(`readyState=${target.readyState}`);
        }
        const keys = Object.keys(e);
        if (keys.length)
            parts.push(`keys=[${keys.join(',')}]`);
        return parts.length ? parts.join(' | ') : Object.prototype.toString.call(e);
    }
    catch (x) {
        return 'serialize-failed';
    }
};
/**
 * 입장 실패 오류를 "사용자에게 보여줄 문자열"로 정규화한다.
 * 원본 상세(Event 등)는 호출부에서 serializeError 로 콘솔에 남기고, 사용자에겐 친화 메시지만 보여준다.
 * (과거 err?.message || err 로 두면 DOM Event 가 그대로 흘러 alert 에 "[object Event]" 로 표시되던 문제 방지)
 */
const toEnterErrorMessage = (_err) => {
    return '강의 입장에 실패했습니다. 잠시 후 다시 시도해 주세요.';
};
/**
 * (ACTION) 임시 방 입장
 */
export const tempEnterRoom = async (data) => {
    return APP.API.searchRoom(data, false, true).then(async (response) => {
        if (response.complete) {
            await tempLogin();
            if (response.data.start_time) {
                const start_time = Date.parse(response.data.start_time);
                response.data.start_time = start_time;
            }
            const data = {
                ...response.data.room,
                start_time: response.data.start_time,
            };
            APP.store.dispatch(setRoomOption(data));
            return { complete: true, data };
        }
        return { complete: false, data: response.data };
    });
};
/**
 * (ACTION) 방 생성
 */
export const createRoom = (name) => {
    return APP.API.createRoom(name).then(response => {
        if (response.complete) {
            const room_uuid = response.data;
            if (room_uuid)
                return tempEnterRoom({ uuid: room_uuid });
            else
                return { complete: false, data: 'api.defaultError' };
        }
        return { complete: false, data: response.data };
    });
};
export const enterRoom = () => {
    return new Promise(async (resolve, reject) => {
        // [C1] 입장 단계별 타이밍 계측 — 배포 후 콘솔에서 병목 구간 확인용. 진단 완료 후 제거.
        const _c1Start = performance.now();
        const _c1 = (label) => console.log(`[C1입장] ${label} +${Math.round(performance.now() - _c1Start)}ms`);
        try {
            const { getState } = APP.store;
            // 중복접속 체크와 비밀번호 체크는 서로 독립(둘 다 read-only 사전 체크)이라 병렬 실행해
            // 입장 전 1 RTT 절약. 판정 순서(occupied → password)는 기존과 동일하게 유지한다.
            // (checkRoomPassword 는 getRoom 조회뿐이라 occupied 실패 시 결과를 버려도 부수효과 없음)
            const [occupideResponse, passwordCheck] = await Promise.all([
                checkOccupiedUser(),
                checkRoomPassword(getState),
            ]);
            _c1('pre-checks 병렬 (occupied+password)');
            if (!occupideResponse.complete) {
                // Redirect to lobby if necessary
                return resolve(occupideResponse);
            }
            if (!passwordCheck.complete) {
                return resolve(passwordCheck);
            }
            if (passwordCheck.message) {
                // Room requires password, return message
                return resolve({ complete: false, message: 'room.passwordChecking' });
            }
            // No password required - join the room
            APP.management
                .join()
                .then(response => {
                _c1('management.join 완료 (총 입장 시간)');
                resolve({ complete: true, message: response });
            })
                .catch(err => {
                // ★ err 이 문자열이 아닌 Event/객체(모바일에서 WebSocket/미디어 오류가 DOM Event 로 옴)면
                //   그대로 message 에 넣으면 alert 에 "[object Event]" 로 표시된다. 문자열만 내보내고
                //   원본은 콘솔에 남겨 진단한다.
                console.error('[enterRoom] management.join failed:', serializeError(err), err);
                resolve({
                    complete: false,
                    message: toEnterErrorMessage(err),
                });
            });
        }
        catch (err) {
            // Handle errors
            console.error('[enterRoom] unexpected error:', serializeError(err), err);
            resolve({ complete: false, message: toEnterErrorMessage(err) });
        }
    });
};
// 방 상태 가져오기
const getRoomState = (stateful) => {
    const state = toState(stateful);
    return state['features/room'];
};
/**
 * 사용자 방에서의 권한
 * @param {IStore['getState']} stateful
 * @returns
 */
export function getPermissions(stateful) {
    return getRoomState(stateful).permissions;
}
/**
 * 방 권한 체크
 * @param {IStore['getState']} stateful
 * @param {string} permission
 * @returns
 */
export function isCheckPermission(stateful, permission) {
    const permissions = getPermissions(stateful);
    return permissions.includes(permission);
}
/**
 * 현재 방 옵션 가져오기 ,
 * @param {IStore['getState']} stateful
 * @returns
 */
export function getRoomOption(stateful) {
    return getRoomState(stateful).option;
}
export function getRoomName(stateful) {
    const activeGroupId = getActiveGroupId(stateful);
    if (activeGroupId !== '') {
        return activeGroupId;
    }
    return getRoomOption(stateful)?.name;
}
/**
 * 방 비밀번호 확인
 * @param stateful
 * @returns
 */
export function checkRoomPassword(stateful) {
    const { uuid } = getRoomOption(stateful);
    return APP.API.getRoom({ uuid }).then((response) => {
        if (response.complete) {
            const data = response.data.items;
            return { complete: true, message: data[0].password_checking };
        }
        return { complete: false, message: 'api.defaultError' };
    });
}
/**
 * 방 미디어 정보 가져오기
 * @param stateful
 * @returns
 */
export function getMediaServerInfo(stateful) {
    return getRoomState(stateful).media_server_info;
}
/**
 * 현재 방에 들어와 있는지에 대한 여부
 * @param {*} stateful
 * @returns
 */
export function isRoomJoined(stateful) {
    return getRoomState(stateful).joined;
}
/**
 * 현재 방에 입장한 그룹 아이디
 * @param {*} stateful
 * @returns
 */
export function getActiveGroupId(stateful) {
    return getRoomState(stateful).group_id;
}
